import React, { useEffect, useRef } from 'react';
import { Trophy, ChevronRight, Sparkles, Gift, Flame } from 'lucide-react';
import { RitualIcon } from './icons/RitualIcon';
interface DailyProgressWidgetProps {
  completed: number;
  total: number;
  onViewTargets: () => void;
}
export const DailyProgressWidget: React.FC<DailyProgressWidgetProps> = ({
  completed,
  total,
  onViewTargets
}) => {
  // Calculate percentage for the progress bar
  const progressPercentage = completed / total * 100;
  // Calculate the circumference of the circle
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasStreak = true; // This would be determined by your app's state
  const streakCount = 3; // This would be determined by your app's state
  // Scroll the rewards container horizontally with mouse wheel
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (scrollContainerRef.current) {
        e.preventDefault();
        scrollContainerRef.current.scrollLeft += e.deltaY;
      }
    };
    const element = scrollContainerRef.current;
    if (element) {
      element.addEventListener('wheel', handleWheel, {
        passive: false
      });
    }
    return () => {
      if (element) {
        element.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);
  // Dynamic message based on user progress
  const getMessage = () => {
    if (completed === 0) {
      return 'Start your streak today. Your first reward is waiting.';
    } else if (completed === 1) {
      return "1 down, 29 to go. Don't break your momentum.";
    } else if (hasStreak) {
      return `🔥 ${streakCount}-day streak! You're building a legend.`;
    } else if (completed === total - 1) {
      return "Only 1 more ritual for today's lootbox!";
    } else {
      return 'Keep going! Every ritual brings special rewards.';
    }
  };
  const handleNavigateToRitualBoard = () => {
    // Dispatch an event to navigate to the ritual board
    const event = new CustomEvent('navigate', {
      detail: {
        section: 'rituals'
      }
    });
    document.dispatchEvent(event);
  };
  return <div className="w-full p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm mb-4 animate-in fade-in duration-300 overflow-hidden" data-id="element-6510">
      <div className="flex items-start gap-4" data-id="element-6511">
        {/* Circular progress meter */}
        <div className="relative w-16 h-16 flex-shrink-0" data-id="element-6512">
          <svg className="w-full h-full" viewBox="0 0 100 100" data-id="element-6513">
            {/* Background circle */}
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-200 dark:text-gray-800" data-id="element-6514" />
            {/* Progress circle with gradient */}
            <circle cx="50" cy="50" r="40" fill="none" stroke="url(#ritualGradient)" strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference - circumference * progressPercentage / 100} className="transform -rotate-90 origin-center transition-all duration-700" style={{
            filter: progressPercentage > 0 ? 'drop-shadow(0 0 3px rgba(236, 72, 153, 0.5))' : 'none'
          }} data-id="element-6515" />
            {/* Gradient definition */}
            <defs data-id="element-6516">
              <linearGradient id="ritualGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-6517">
                <stop offset="0%" stopColor="#ec4899" data-id="element-6518" />
                <stop offset="100%" stopColor="#8b5cf6" data-id="element-6519" />
              </linearGradient>
            </defs>
          </svg>
          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-6520">
            <RitualIcon size={20} state={completed > 0 ? 'active' : 'default'} data-id="element-6521" />
            <div className="text-xs font-medium mt-1" data-id="element-6522">{completed}/30</div>
          </div>
          {/* Animated sparks around the circle when progress is made */}
          {progressPercentage > 0 && <>
              <span className="absolute top-0 left-1/2 w-1 h-1 bg-pink-400 rounded-full animate-spark-float" style={{
            '--delay': '0s'
          } as React.CSSProperties} data-id="element-6523"></span>
              <span className="absolute top-1/4 right-0 w-1.5 h-1.5 bg-purple-400 rounded-full animate-spark-float" style={{
            '--delay': '0.3s'
          } as React.CSSProperties} data-id="element-6524"></span>
              <span className="absolute bottom-1/4 left-0 w-1 h-1 bg-pink-400 rounded-full animate-spark-float" style={{
            '--delay': '0.6s'
          } as React.CSSProperties} data-id="element-6525"></span>
            </>}
        </div>
        <div className="flex-1" data-id="element-6526">
          <div data-id="element-6527">
            <h3 className="font-semibold text-base bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent flex items-center" data-id="element-6528">
              Your Ritual Journey Today
              <Sparkles size={16} className="ml-2 text-pink-500 animate-pulse-subtle" data-id="element-6529" />
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-6530">
              Unlock rewards, streaks, and Angel perks by completing your
              rituals.
            </p>
          </div>
          {/* Dynamic message */}
          <div className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-6531">
            {getMessage()}
          </div>
          {/* Scrollable reward cards */}
          <div ref={scrollContainerRef} className="mt-3 flex gap-3 overflow-x-auto pb-2 scrollbar-hide" data-id="element-6532">
            <div className="flex-shrink-0 w-32 p-2 bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg border border-amber-200/50 dark:border-amber-800/30" data-id="element-6533">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-6534">
                <Flame size={14} className="text-amber-500" data-id="element-6535" />
                <span className="text-xs font-medium text-amber-800 dark:text-amber-300" data-id="element-6536">
                  Daily Streak
                </span>
              </div>
              <p className="text-[10px] text-amber-700 dark:text-amber-400" data-id="element-6537">
                50 Gems reward
              </p>
            </div>
            <div className="flex-shrink-0 w-32 p-2 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200/50 dark:border-blue-800/30" data-id="element-6538">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-6539">
                <Gift size={14} className="text-blue-500" data-id="element-6540" />
                <span className="text-xs font-medium text-blue-800 dark:text-blue-300" data-id="element-6541">
                  Weekly Ritual
                </span>
              </div>
              <p className="text-[10px] text-blue-700 dark:text-blue-400" data-id="element-6542">
                Mystery Lootbox
              </p>
            </div>
            <div className="flex-shrink-0 w-32 p-2 bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200/50 dark:border-purple-800/30" data-id="element-6543">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-6544">
                <Trophy size={14} className="text-purple-500" data-id="element-6545" />
                <span className="text-xs font-medium text-purple-800 dark:text-purple-300" data-id="element-6546">
                  Premium Ritual
                </span>
              </div>
              <p className="text-[10px] text-purple-700 dark:text-purple-400" data-id="element-6547">
                Exclusive Angel Skin
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* CTA Button */}
      <div className="mt-3 flex justify-end" data-id="element-6548">
        <button onClick={handleNavigateToRitualBoard} className="px-4 py-2 text-sm bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-full hover:shadow-md hover:shadow-pink-500/20 transition-all flex items-center gap-1" data-id="element-6549">
          <Sparkles size={14} className="text-pink-200" data-id="element-6550" />
          <span data-id="element-6551">Complete Today's Rituals</span>
          <ChevronRight size={16} data-id="element-6552" />
        </button>
      </div>
    </div>;
};